// Error Boundary Component for better error handling

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    logger.error('ErrorBoundary caught error', error, {
      componentStack: errorInfo.componentStack
    });

    // ChunkLoadError: tritt auf, wenn nach einem Deployment ein alter Chunk-Hash
    // im SW-Cache liegt, aber das neue Index.html andere Chunks erwartet.
    // → Service-Worker-Caches leeren und Seite einmal neu laden (Endlosschleife per Sentinel verhindern).
    const isChunkLoadError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error?.message || '') ||
      /Loading CSS chunk/i.test(error?.message || '');

    if (isChunkLoadError && typeof window !== 'undefined') {
      const sentinel = 'chunk_reload_attempt';
      const already = sessionStorage.getItem(sentinel);
      if (!already) {
        sessionStorage.setItem(sentinel, '1');
        this.clearCachesAndReload();
        return;
      }
      // Wenn der Reload nicht geholfen hat, normalen Fehler anzeigen.
      sessionStorage.removeItem(sentinel);
    }

    this.setState({ error, errorInfo });
  }

  private async clearCachesAndReload() {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      console.warn('Cache/SW cleanup failed', e);
    }
    window.location.reload();
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '32px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{
            maxWidth: '600px',
            background: 'white',
            padding: '48px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>😕</div>
            <h1 style={{
              fontSize: '28px',
              marginBottom: '16px',
              color: '#333',
              fontWeight: 600
            }}>
              Etwas ist schief gelaufen
            </h1>
            <p style={{
              fontSize: '16px',
              marginBottom: '24px',
              color: '#666',
              lineHeight: 1.6
            }}>
              Die Anwendung ist auf einen unerwarteten Fehler gestoßen.
              Bitte versuchen Sie, die Seite neu zu laden.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginBottom: '24px',
                textAlign: 'left',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                <summary style={{ cursor: 'pointer', marginBottom: '12px', fontWeight: 600 }}>
                  Fehlerdetails (nur in Entwicklung sichtbar)
                </summary>
                <pre style={{
                  overflow: 'auto',
                  fontSize: '12px',
                  color: '#d32f2f',
                  margin: 0
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '12px 24px',
                  background: '#5a8c14',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#4a7210'}
                onMouseOut={(e) => e.currentTarget.style.background = '#5a8c14'}
              >
                Erneut versuchen
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  color: '#5a8c14',
                  border: '2px solid #5a8c14',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                Zur Startseite
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
